
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Comment } from '@/types/board';
import { addComment } from '@/services/postService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CommentItem } from './CommentItem';

interface CommentsSectionProps {
  postId: string;
  comments: Comment[];
  isLoading: boolean;
  user: any;
  formatDate: (date: string) => string;
}

export const CommentsSection = ({ 
  postId, 
  comments, 
  isLoading, 
  user, 
  formatDate 
}: CommentsSectionProps) => {
  const [commentText, setCommentText] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const commentMutation = useMutation({
    mutationFn: () => {
      if (!postId || !user) return Promise.reject('Missing data');
      return addComment(postId, commentText, user);
    },
    onSuccess: () => {
      setCommentText('');
      queryClient.invalidateQueries({ queryKey: ['comments', postId] });
      queryClient.invalidateQueries({ queryKey: ['post', postId] });
      toast({
        title: "댓글이 작성되었습니다",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "댓글 작성 실패",
        description: "오류가 발생했습니다. 다시 시도해주세요."
      });
    }
  });
  
  const handleAddComment = () => {
    if (!commentText.trim() || !user) return;
    commentMutation.mutate();
  };

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold mb-4">댓글 {comments.length}개</h3>
      
      {user ? (
        <div className="mb-6">
          <Textarea
            placeholder="댓글을 입력하세요..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className="mb-2"
          />
          <div className="flex justify-end">
            <Button 
              onClick={handleAddComment} 
              disabled={commentMutation.isPending || !commentText.trim()}
            >
              {commentMutation.isPending ? '작성 중...' : '댓글 작성'}
            </Button>
          </div>
        </div>
      ) : (
        <Card className="mb-6 p-4 text-center">
          <p className="text-muted-foreground mb-2">댓글을 작성하려면 로그인이 필요합니다.</p>
          <Button asChild>
            <Link to="/login">로그인하기</Link>
          </Button>
        </Card>
      )}
      
      {isLoading ? (
        <Card className="p-8 text-center">
          <p>댓글을 불러오는 중...</p>
        </Card>
      ) : comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <CommentItem 
              key={comment.id} 
              comment={comment} 
              formatDate={formatDate} 
              postId={postId}
              user={user}
            />
          ))}
        </div>
      ) : (
        <Card className="p-8 text-center text-muted-foreground">
          아직 댓글이 없습니다. 첫 댓글을 남겨보세요!
        </Card>
      )}
    </div>
  );
};
